/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { FaImage, FaVideo, FaMapMarkerAlt } from "react-icons/fa";
import {
  CardWrapper,
  VideoUploadSection,
  ImageUploadSection,
  InputWithUnit,
  BoxSelector,
  SubmitButton,
  RadioField,
  PageHeader,
  InputField,
  SmallInputField,
  LocationSelector,
} from "../../../../components/admin/common/form-elements";
import { AREA_UNITS, INITIAL_LOCATION } from "../../../../utils/constants";
import { showError, showSuccess } from "../../../../utils/toastUtils";
import api from "../../../../configs/api";
import { useNavigate, useParams } from "react-router-dom";
import AddLandPropertyPageSkeleton from "../../../../components/admin/skeleton-loaders/properties/AddLandPropertyPageSkeleton";

const AddLandPropertyPage = () => {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const initialFormData = {
    propertyName: "",
    videoUrl: "",
    images: [],
    isPreleasedProperty: false,
    floorsAllowed: false,
    carpetArea: "",
    carpetAreaUnits: AREA_UNITS,
    expectedPrice: "",
    pricePerSqft: "",
    openSides: "",
    roadWidth: "",
    roadWidthUnits: AREA_UNITS,
    location: "",
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState(initialFormData);

  // useEffect(() => {
  //   console.log("formData", formData);
  // }, [formData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (isEditMode && id) {
      const fetchProperty = async () => {
        setIsLoading(true);
        try {
          const resp = await api.get(`/properties/${id}`);
          const data = resp?.data?.data;

          // console.log("Land property fetched:", data);

          setFormData({
            ...initialFormData,
            propertyName: data.title || "",
            videoUrl: data.videos?.[0] || "",
            images: data.images || [],

            isPreleasedProperty: data.property?.isPreleasedProperty
              ? "true"
              : "false",
            floorsAllowed: data.property?.floorsAllowedForConstruction ?? "",
            carpetArea: data.property?.carpetArea?.value ?? "",
            carpetAreaUnits: data.property?.carpetArea?.unit || "sqft",
            expectedPrice: data.property?.expectedPrice ?? "",
            pricePerSqft: data.property?.pricePerSqft ?? "",
            openSides: Number(data.property?.noOfOpenSides) ?? "",
            roadWidth: data.property?.widthOfRoadFacing.value ?? "",
            roadWidthUnits: data.property?.widthOfRoadFacing.unit ?? "sqft",

            // LOCATION (full object from DB)
            location: data.location || INITIAL_LOCATION,
          });
        } catch (err) {
          showError("Failed to load property details");
        } finally {
          setIsLoading(false);
        }
      };

      fetchProperty();
    }
  }, [id, isEditMode]);

  const validateForm = () => {
    const missing = [];

    if (!formData.propertyName?.trim()) missing.push("Property Name");

    if (!formData.images || formData.images.length < 5)
      missing.push("At least 5 Property Images");

    if (!formData.carpetArea) missing.push("Carpet Area");

    if (!formData.expectedPrice) missing.push("Expected Price");

    if (!formData.pricePerSqft) missing.push("Price per sqft");

    if (!formData.openSides) missing.push("Number of open sides");

    if (!formData.roadWidth) missing.push("Width of Road Facing");

    if (!formData.floorsAllowed && formData.floorsAllowed !== 0)
      missing.push("Floors Allowed for Construction");

    if (
      !formData.location ||
      !formData.location.coordinates ||
      formData.location.coordinates.length < 2
    ) {
      missing.push("Location (Pick from Map)");
    }

    if (missing.length > 0) {
      const firstMissing = missing[0];
      showError(`Please fill out: ${firstMissing}`);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      title: formData?.propertyName,
      type: "land",

      images: formData?.images,
      videos: [formData?.videoUrl],

      location: formData?.location,

      property: {
        isPreleasedProperty: formData?.isPreleasedProperty === "true",
        floorsAllowedForConstruction: formData?.floorsAllowed
          ? Number(formData?.floorsAllowed)
          : 0,
        carpetArea: {
          value: formData?.carpetArea ? Number(formData?.carpetArea) : 0,
          unit: "sqft",
        },
        expectedPrice: formData?.expectedPrice
          ? Number(formData?.expectedPrice)
          : 0,
        pricePerSqft: formData?.pricePerSqft
          ? Number(formData?.pricePerSqft)
          : 0,
        noOfOpenSides: formData?.openSides ? Number(formData?.openSides) : 0,
        widthOfRoadFacing: formData?.roadWidth
          ? Number(formData?.roadWidth)
          : 0,
      },
    };

    setIsSubmitting(true);

    try {
      let resp;

      if (isEditMode) {
        resp = await api.put(`/properties/${id}`, payload);
        showSuccess("Property Updated Successfully");
      } else {
        resp = await api.post(`/properties`, payload);
        showSuccess("Property Created Successfully");
      }

      console.log("Server Response:", resp);
      setFormData(initialFormData);
      navigate(-1);
    } catch (error) {
      showError(error?.response?.data?.message || "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading && isEditMode) {
    return <AddLandPropertyPageSkeleton />;
  }
  return (
    <div className="min-h-screen  bg-background font-poppins">
      {/* Header */}
      <PageHeader title={isEditMode ? "Edit Land / Plot" : "Add Land / Plot"} />
      {/* Main Content */}
      <div className="max-full mx-auto px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-4">
          {/* Left Column */}
          <div className="space-y-6">
            <CardWrapper>
              <InputField
                label="Property name"
                name="propertyName"
                value={formData.propertyName}
                onChange={handleInputChange}
                placeholder="E.g. Green Valley Plot"
              />
            </CardWrapper>

            {/* Add Vidoes */}
            <CardWrapper
              title="About your project"
              subtitle="A video is worth a thousand pictures. Properties with videos get higher page views"
            >
              <VideoUploadSection
                icon={FaVideo}
                prefillData={formData.videoUrl ? [formData.videoUrl] : []}
                onFileSelect={(videoUrl) => {
                  setFormData((prev) => ({ ...prev, videoUrl }));
                }}
                buttonText="+ Upload Video"
                description="Upload video of min size 20 MB in format .mp4 video duration should be less than 10 min`s."
              />
            </CardWrapper>

            {/* Add Photos */}
            <CardWrapper
              title="Add photos of your project"
              subtitle="A picture is worth a thousand words. 80% of buyers look at photos before buying."
            >
              <ImageUploadSection
                icon={FaImage}
                prefillData={formData?.images}
                onChange={(uploadedImages) => {
                  setFormData((prev) => ({ ...prev, images: uploadedImages }));
                }}
                buttonText="↓ Add atleast 5 photos"
                description="Minimum 5 photos | Max size 10MB | Formats: png, jpg, gif, webp, heic, heif"
                badge="5"
              />
            </CardWrapper>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Pre-Leased Property */}
            <CardWrapper title="Pre-Leased Property">
              <RadioField
                name={`isPreleasedProperty `}
                onChange={(value) => {
                  setFormData((prev) => ({
                    ...prev,
                    isPreleasedProperty: value,
                  }));
                }}
                value={formData?.isPreleasedProperty}
                options={[
                  { label: "Yes", value: "true" },
                  { label: "No", value: "false" },
                ]}
              />
            </CardWrapper>
            {/* Floor Allowed For Construction */}
            <CardWrapper title={"Max. Floors allowed Construction"}>
              <SmallInputField
                type="number"
                name="floorsAllowed"
                value={formData.floorsAllowed}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    floorsAllowed: Number(e.target.value),
                  }));
                }}
                placeholder=" Max. Floors allowed, e.g- 4 or 5"
              />
            </CardWrapper>
            {/* Floor Allowed For Construction Details*/}
            <CardWrapper title="Plot Details">
              {/* Carpet Area */}
              <div className="mb-4">
                <InputWithUnit
                  label={"Carpet Area"}
                  type="number"
                  name="carpetArea"
                  value={formData.carpetArea}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      carpetArea: Number(e.target.value),
                    }));
                  }}
                  placeholder="Carpet Area"
                />
              </div>

              {/* Price Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <SmallInputField
                  label={"Expected Price"}
                  type="number"
                  name="expectedPrice"
                  value={formData.expectedPrice}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      expectedPrice: Number(e.target.value),
                    }));
                  }}
                  placeholder="₹ Expected Price"
                />
                <SmallInputField
                  label={"Price per sqft"}
                  type="number"
                  name="pricePerSqft"
                  value={formData.pricePerSqft}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      pricePerSqft: Number(e.target.value),
                    }));
                  }}
                  placeholder="₹ Price per sqft."
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                <BoxSelector
                  totalBoxes={4}
                  label="No. of open sides"
                  value={formData.openSides}
                  onChange={(value) => {
                    setFormData((prev) => ({ ...prev, openSides: value }));
                  }}
                />
                <InputWithUnit
                  label={`Width of Road Facing`}
                  type="number"
                  name="roadWidth"
                  value={formData.roadWidth}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      roadWidth: Number(e.target.value),
                    }));
                  }}
                  placeholder="Road Facing Width"
                />
              </div>
            </CardWrapper>
            {/* Fetch Location */}
            <CardWrapper title="Property Location">
              <LocationSelector
                value={formData.location}
                onChange={(updatedLocation) =>
                  setFormData((prev) => ({
                    ...prev,
                    location: updatedLocation,
                  }))
                }
              />
            </CardWrapper>
            {/* Submit Button */}
            <SubmitButton
              isLoading={isSubmitting}
              onClick={handleSubmit}
              label={isEditMode ? "Save Changes" : "Add Property"}
            />{" "}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddLandPropertyPage;

// import { useState } from "react";
// import { FaImage, FaVideo, FaMapMarkerAlt } from "react-icons/fa";
// import {
//   CardWrapper,
//   VideoUploadSection,
//   ImageUploadSection,
//   InputWithSelect,
//   InputWithUnit,
//   BoxSelector,
//   SubmitButton,
//   RadioField,
//   PageHeader,
//   InputField,
//   SmallInputField,
// } from "../../../../components/admin/common/form-elements";
// import { AREA_UNITS } from "../../../../utils/constants";
// import { showError, showSuccess } from "../../../../utils/toastUtils";
// import api from "../../../../configs/api";
// import { useNavigate } from "react-router-dom";

// const AddLandPropertyPage = () => {
//   const navigate = useNavigate();
//   const initialData = {
//     propertyName: "",
//     videoUrl: "",
//     images: [],
//     isPreleasedProperty : false,
//     floorsAllowed: false,
//     carpetArea: "",
//     carpetAreaUnits: AREA_UNITS,
//     expectedPrice: "",
//     pricePerSqft: "",
//     openSides: "",
//     roadWidth: "",
//     roadWidthUnits: AREA_UNITS,
//     location: "",
//   };
//   const [isLoading, setIsLoading] = useState(false);
//   const [formData, setFormData] = useState(initialData);

//   // useEffect(() => {
//   //   console.log("formData", formData);
//   // }, [formData]);

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({
//       ...prev,
//       [name]: value,
//     }));
//   };

//   const validateForm = () => {
//     const missing = [];

//     if (!formData.propertyName?.trim()) missing.push("Property Name");
//     if (!formData.location?.trim()) missing.push("Location");

//     if (!formData.images || formData.images.length < 4)
//       missing.push("At least 5 Property Images");

//     if (missing.length > 0) {
//       const firstMissing = missing[0];
//       showError(`Please fill out: ${firstMissing}`);
//       return false;
//     }
//     return true;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!validateForm()) return;

//     const payload = {
//       title: formData?.propertyName,
//       type: "land",

//       images: formData?.images,
//       videos: [formData?.videoUrl],

//       location: {
//         address: formData?.location,
//       },

//       property: {
//         isPreleasedProperty : formData?.isPreleasedProperty  === "true",
//         floorsAllowedForConstruction: formData?.floorsAllowed
//           ? Number(formData?.floorsAllowed)
//           : 0,
//         carpetArea: {
//           value: formData?.carpetArea ? Number(formData?.carpetArea) : 0,
//           unit: "sqft",
//         },
//         expectedPrice: formData?.expectedPrice
//           ? Number(formData?.expectedPrice)
//           : 0,
//         pricePerSqft: formData?.pricePerSqft
//           ? Number(formData?.pricePerSqft)
//           : 0,
//         noOfOpenSides: formData?.openSides ? Number(formData?.openSides) : 0,
//         widthOfRoadFacing: formData?.roadWidth
//           ? Number(formData?.roadWidth)
//           : 0,
//       },
//     };

//     setIsLoading(true);

//     try {
//       console.log("Payload:", payload);
//       const resp = await api.post(`/properties`, payload);
//       console.log("resp:", resp);
//       showSuccess("Property Created Successfully");
//       setFormData(initialData);
//       navigate(-1);
//     } catch (error) {
//       showError(error?.response?.data?.message || "Property Couldn't be added");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   return (
//     <div className="min-h-screen  bg-background font-poppins">
//       {/* Header */}
//       <PageHeader title={`Add Land / Plot`} />

//       {/* Main Content */}
//       <div className="max-full mx-auto px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6">
//         <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-4">
//           {/* Left Column */}
//           <div className="space-y-6">
//             <CardWrapper>
//               <InputField
//                 label="Property name"
//                 name="propertyName"
//                 value={formData.propertyName}
//                 onChange={handleInputChange}
//                 placeholder="E.g. Green Valley Plot"
//               />
//             </CardWrapper>

//             {/* Add Vidoes */}
//             <CardWrapper
//               title="About your project"
//               subtitle="A video is worth a thousand pictures. Properties with videos get higher page views"
//             >
//               <VideoUploadSection
//                 icon={FaVideo}
//                 onFileSelect={(videoUrl) => {
//                   setFormData((prev) => ({ ...prev, videoUrl }));
//                 }}
//                 buttonText="+ Upload Video"
//                 description="Upload video of min size 20 MB in format .mp4 video duration should be less than 10 min`s."
//               />
//             </CardWrapper>

//             {/* Add Photos */}
//             <CardWrapper
//               title="Add photos of your project"
//               subtitle="A picture is worth a thousand words. 80% of buyers look at photos before buying."
//             >
//               <ImageUploadSection
//                 icon={FaImage}
//                 onChange={(uploadedImages) => {
//                   setFormData((prev) => ({ ...prev, images: uploadedImages }));
//                 }}
//                 buttonText="↓ Add atleast 5 photos"
//                 description="Minimum 5 photos | Max size 10MB | Formats: png, jpg, gif, webp, heic, heif"
//                 badge="5"
//               />
//             </CardWrapper>
//           </div>

//           {/* Right Column */}
//           <div className="space-y-6">
//             {/* Pre-Leased Property */}
//             <CardWrapper title="Pre-Leased Property">
//               <RadioField
//                 name={`isPreleasedProperty `}
//                 onChange={(value) => {
//                   setFormData((prev) => ({
//                     ...prev,
//                     isPreleasedProperty : value,
//                   }));
//                 }}
//                 value={formData?.isPreleasedProperty }
//                 options={[
//                   { label: "Yes", value: "true" },
//                   { label: "No", value: "false" },
//                 ]}
//               />
//             </CardWrapper>

//             {/* Floor Allowed For Construction */}
//             <CardWrapper title={"Floor Allowed For Construction"}>
//               <InputWithSelect
//                 name="floorsAllowed"
//                 placeholder="Enter floor allowed or not?"
//                 selectName="floorsAllowed"
//                 selectValue={formData?.floorsAllowed}
//                 selectOptions={[
//                   { label: "Yes", value: true },
//                   { label: "No", value: false },
//                 ]}
//                 onSelectChange={(value) => {
//                   setFormData({ ...formData, floorsAllowed: value === "true" });
//                 }}
//                 disabled={false}
//               />
//             </CardWrapper>

//             {/* Floor Allowed For Construction Details*/}
//             <CardWrapper title="Floor Allowed For Construction Details">
//               <fieldset
//                 disabled={!formData?.floorsAllowed}
//                 className={`space-y-4 ${
//                   !formData?.floorsAllowed
//                     ? "opacity-50 pointer-events-none"
//                     : "opacity-100"
//                 }`}
//               >
//                 {/* Carpet Area */}
//                 <div className="mb-4">
//                   <InputWithUnit
//                     label={"Carpet Area"}
//                     type="number"
//                     name="carpetArea"
//                     value={formData.carpetArea}
//                     onChange={(e) => {
//                       setFormData((prev) => ({
//                         ...prev,
//                         carpetArea: Number(e.target.value),
//                       }));
//                     }}
//                     placeholder="Carpet Area"
//                   />
//                 </div>

//                 {/* Price Fields */}
//                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
//                   <SmallInputField
//                     label={"Expected Price"}
//                     type="number"
//                     name="expectedPrice"
//                     value={formData.expectedPrice}
//                     onChange={(e) => {
//                       setFormData((prev) => ({
//                         ...prev,
//                         expectedPrice: Number(e.target.value),
//                       }));
//                     }}
//                     placeholder="₹ Expected Price"
//                   />
//                   <SmallInputField
//                     label={"Price per sqft"}
//                     type="number"
//                     name="pricePerSqft"
//                     value={formData.pricePerSqft}
//                     onChange={(e) => {
//                       setFormData((prev) => ({
//                         ...prev,
//                         pricePerSqft: Number(e.target.value),
//                       }));
//                     }}
//                     placeholder="₹ Price per sqft."
//                   />
//                 </div>

//                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
//                   <BoxSelector
//                     totalBoxes={4}
//                     label="No. of open sides"
//                     value={formData.openSides}
//                     onChange={(value) => {
//                       setFormData((prev) => ({ ...prev, openSides: value }));
//                     }}
//                   />
//                   <InputWithUnit
//                     label={`Width of Road Facing`}
//                     type="number"
//                     name="roadWidth"
//                     value={formData.roadWidth}
//                     onChange={(e) => {
//                       setFormData((prev) => ({
//                         ...prev,
//                         roadWidth: Number(e.target.value),
//                       }));
//                     }}
//                     placeholder="Road Facing Width"
//                   />
//                 </div>
//               </fieldset>
//             </CardWrapper>

//             {/* Fetch Location */}
//             <CardWrapper title={`Fetch Location`}>
//               <div className="relative">
//                 <input
//                   type="text"
//                   name="location"
//                   value={formData.location}
//                   onChange={handleInputChange}
//                   placeholder="Enter location"
//                   className="w-full px-4 py-3 pr-12 border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary text-sm"
//                 />
//                 <button
//                   type="button"
//                   className="absolute right-3 top-1/2 -translate-y-1/2 text-primary hover:text-primary/80 transition-colors"
//                 >
//                   <FaMapMarkerAlt className="w-5 h-5" />
//                 </button>
//               </div>
//             </CardWrapper>

//             {/* Submit Button */}
//             <SubmitButton isLoading={isLoading} onClick={handleSubmit} />
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default AddLandPropertyPage;
